import React from 'react';
import {
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  TextFieldProps,
  Typography,
} from '@material-ui/core';
import ActionsButtonsBaseModal from 'components/atoms/ActionsButtonsBaseModal';
import ButtonWithLoading from 'components/atoms/ButtonWithLoading';
import LoadingInputEndAdornment from 'components/atoms/LoadingInputEndAdornment';
import InputDuration from 'components/atoms/InputDuration';
import { IOption } from 'components/molecules/AnomalyGrid/types';
import { useFormik } from 'formik';
import Autocomplete, {
  createFilterOptions,
} from '@material-ui/lab/Autocomplete';
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import DayjsUtils from '@date-io/dayjs';
import ptBR from 'dayjs/locale/pt-br';
import {
  getActivityMaintenanceType,
  getYesOrNotOption,
} from 'services/activity';
import { useQuery } from 'react-query';
import dayjs from 'dayjs';
import * as Yup from 'yup';
import { debounce } from 'lodash';
import { postExecutorOptions } from 'services/executor';
import { postAnomalyPlan } from 'services/anomaly';
import { removeSpecialChar } from 'helpers/removeSpecialChar';
import { IModalPlanProps } from './types';
import useStyles from './style';

const FORM_ID = 'ANOMALY_FORM_MODAL_PLAN';

const DEFAULT_HELPER_TEXT = 'Obrigatório';
const DEFAULT_ERROR_TEXT = 'Preenchimento inválido';
const NO_OPTIONS_TEXT = 'Nenhum resultado encontrado';
const SEARCH_GUIDE_TEXT = 'Digite 2 Caracteres ou mais para pesquisar';
const OPTIONAL = 'Opcional';
const MIN_DATE = dayjs(dayjs().format('YYYY-MM-DD'));
const MAX_DATE = dayjs('2100-01-01');

const ModalPlan = ({
  open,
  onClose,
  children,
  gridRow,
  reloadData,
}: IModalPlanProps) => {
  const classes = useStyles();
  const [loadingSaveForm, setLoadingSaveForm] = React.useState<boolean>(false);
  const [typeMaintenanceOptions, setTypeMaintenanceOptions] = React.useState<
    IOption[]
  >([]);
  const [supplierOptions, setSupplierOptions] = React.useState<IOption[]>([]);
  const [outageOptions, setOutageOptions] = React.useState<IOption[]>([]);
  const [utilizationOptions, setUtilizationOptions] = React.useState<IOption[]>(
    [],
  );
  const [fieldsSearchText, setFieldsSearchText] = React.useState<{
    [key: string]: string;
  }>({});
  const filter = createFilterOptions<IOption>();

  const validationSchema = Yup.object({
    maintenanceId: Yup.number().required(DEFAULT_HELPER_TEXT),
    plannedStart: Yup.mixed()
      .required(DEFAULT_HELPER_TEXT)
      .test('is-greater-than', DEFAULT_ERROR_TEXT, value => {
        const test = dayjs(value);
        return (
          dayjs(value).isValid() &&
          (test.isSame(MIN_DATE) || test.isAfter(MIN_DATE))
        );
      }),
    plannedCompletion: Yup.mixed()
      .required(DEFAULT_HELPER_TEXT)
      // eslint-disable-next-line func-names
      .test('is-greater-than', DEFAULT_ERROR_TEXT, function (value) {
        const test = dayjs(value);
        // eslint-disable-next-line react/no-this-in-sfc
        const { plannedStart } = this.parent;
        return (
          dayjs(value).isValid() &&
          (test.isSame(plannedStart) || test.isAfter(plannedStart))
        );
      }),
    supplier: Yup.string().required(DEFAULT_HELPER_TEXT),
    frequency: Yup.number().positive(DEFAULT_ERROR_TEXT).nullable(),
    outage: Yup.string().required(DEFAULT_HELPER_TEXT),
    utilization: Yup.string().required(DEFAULT_HELPER_TEXT),
    planNumber: Yup.string().required().typeError(DEFAULT_HELPER_TEXT),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      activityName: gridRow?.title,
      maintenanceId: '',
      plannedStart: null,
      plannedCompletion: null,
      plannedDuration: '',
      supplier: '',
      frequency: null,
      outage: '',
      utilization: '',
      planNumber: dayjs().year(),
    },
    validationSchema,
    onSubmit: formValues => {
      const { ...values } = formValues;
      let plannedDuration = 0;
      if (formValues.plannedDuration) {
        const [hours, minutes] = formValues.plannedDuration.split(':');

        plannedDuration = Number(
          dayjs
            .duration({
              hours: Number(hours),
              minutes: Number(minutes),
            })
            .asHours()
            .toFixed(2),
        );
      }

      const anomalyId = gridRow?.id;

      const supplier = removeSpecialChar(formik.values.supplier);

      setLoadingSaveForm(true);
      postAnomalyPlan({ ...values, plannedDuration, anomalyId, supplier })
        .then(() => {
          if (reloadData) reloadData();
          onClose();
        })
        .finally(() => setLoadingSaveForm(false));
    },
  });

  const enableQueries = React.useMemo(() => open, [open]);

  const enableTypeMaintenanceOptions = React.useMemo(
    () => enableQueries,
    [enableQueries],
  );

  const enableSupplierOptions = React.useMemo(() => {
    return (
      enableQueries &&
      Boolean(
        fieldsSearchText?.supplier && fieldsSearchText?.supplier.length >= 2,
      )
    );
  }, [enableQueries, fieldsSearchText?.supplier]);

  const enableOutageOptions = React.useMemo(
    () => enableQueries,
    [enableQueries],
  );

  const enableUtilizationOptions = React.useMemo(
    () => enableQueries,
    [enableQueries],
  );

  const queryMaintenanceTypeOptions = useQuery({
    queryKey: ['typeMaintenanceOptions'],
    queryFn: async () => {
      const { data } = await getActivityMaintenanceType({});

      return data;
    },
    select: data => {
      return data.map(item => {
        return {
          label: item?.description,
          value: item?.id,
        };
      });
    },
    onSuccess: (data: IOption[]) => {
      setTypeMaintenanceOptions(data);
    },
    enabled: enableTypeMaintenanceOptions,
  });

  const querySupplierOptions = useQuery({
    queryKey: ['supplierOptionsContext', [fieldsSearchText?.supplier]],
    queryFn: async () => {
      const params = {
        name: fieldsSearchText?.supplier,
      };

      const { data } = await postExecutorOptions(params);
      return data;
    },
    select: data => {
      return data.map(option => {
        return {
          label: option.description,
          value: option.description,
        };
      });
    },
    onSuccess: (data: IOption[]) => {
      setSupplierOptions(data);
    },
    cacheTime: 0,
    enabled: enableSupplierOptions,
  });

  const queryOutageOptions = useQuery({
    queryKey: ['outageOptionsContext'],
    queryFn: async () => {
      const { data } = await getYesOrNotOption({});
      return data;
    },
    select: data => {
      return Object.values(data).map(value => {
        return {
          label: value === 'S' ? 'Sim' : 'Não',
          value,
        };
      });
    },
    onSuccess: (data: IOption[]) => {
      setOutageOptions(data);
    },
    enabled: enableOutageOptions,
  });

  const queryUtilizationOptions = useQuery({
    queryKey: ['utilizationOptionsContext'],
    queryFn: async () => {
      const { data } = await getYesOrNotOption({});
      return data;
    },
    select: data => {
      return Object.values(data).map(value => {
        return {
          label: value === 'S' ? 'Sim' : 'Não',
          value,
        };
      });
    },
    onSuccess: (data: IOption[]) => {
      setUtilizationOptions(data);
    },
    enabled: enableUtilizationOptions,
  });

  const handleDateChange = React.useCallback(
    (name, value) => {
      formik.setFieldTouched(name);
      formik.setFieldValue(name, value);
    },
    [formik],
  );

  const handleAutoCompleteChange = React.useCallback(
    (name: string, option: IOption) => {
      const value = option?.value || '';
      formik.setFieldValue(name, value);
    },
    [formik],
  );

  const debounceSetFieldSearchText = React.useMemo(
    () =>
      debounce((name, value) => {
        setFieldsSearchText(currentValues => ({
          ...currentValues,
          [name]: value,
        }));
      }, 600),
    [],
  );

  const handleFieldSearchTextChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target;
      if (value.length >= 2) {
        debounceSetFieldSearchText(name, value);
      } else {
        debounceSetFieldSearchText(name, '');
      }
    },
    [debounceSetFieldSearchText],
  );

  const getOptionSelected = (option: IOption, anotherOption: IOption) =>
    option.value === anotherOption.value;

  const inputRenderer = React.useCallback(
    (params: TextFieldProps, loading?: boolean) => (
      <TextField
        {...params}
        className={classes.formBaseInput}
        variant="outlined"
        InputProps={{
          ...params.InputProps,
          endAdornment: (
            <>
              <LoadingInputEndAdornment loading={loading} />
              {params?.InputProps?.endAdornment}
            </>
          ),
        }}
      />
    ),
    [classes.formBaseInput],
  );

  React.useEffect(() => {
    if (!open) {
      if (formik.dirty) {
        formik.resetForm();
      }

      if (supplierOptions.length > 0) setSupplierOptions([]);
      if (Object.keys(fieldsSearchText).length > 0) setFieldsSearchText({});
    }
  }, [fieldsSearchText, formik, open, supplierOptions.length]);

  const renderBody = React.useCallback(() => {
    return (
      <form id={FORM_ID} onSubmit={formik.handleSubmit}>
        <Grid container className={classes.bodyContent} spacing={0}>
          <Typography className={classes.formMessage}>
            Preencha os dados obrigatórios para planejar atividade:
          </Typography>
          <Grid item xs={12}>
            <TextField
              id="activityName"
              name="activityName"
              label="Atividade"
              variant="outlined"
              disabled
              value={formik.values.activityName}
              onChange={formik.handleChange}
            />
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Autocomplete
                id="maintenanceId"
                className={classes.inputList}
                options={typeMaintenanceOptions}
                onChange={(e, selected) => {
                  return handleAutoCompleteChange(
                    'maintenanceId',
                    selected as IOption,
                  );
                }}
                getOptionLabel={option => String(option.label)}
                getOptionSelected={getOptionSelected}
                noOptionsText={NO_OPTIONS_TEXT}
                renderInput={params => {
                  const extraProps = {
                    name: 'maintenanceId',
                    label: 'Tipo de manutenção',
                    error:
                      formik.touched.maintenanceId &&
                      Boolean(formik.errors.maintenanceId),
                    helperText:
                      (formik.touched.maintenanceId &&
                        formik.errors.maintenanceId) ||
                      DEFAULT_HELPER_TEXT,
                  };
                  return inputRenderer(
                    { ...params, ...extraProps },
                    queryMaintenanceTypeOptions?.isLoading,
                  );
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                id="planNumber"
                name="planNumber"
                label="Número do Plano"
                variant="outlined"
                helperText={
                  (formik.touched.planNumber && formik.errors.planNumber) ||
                  DEFAULT_HELPER_TEXT
                }
                onBlur={formik.handleBlur}
                inputProps={{
                  type: 'number',
                }}
                onChange={event => {
                  const { value, name } = event.target;

                  if (value) {
                    formik.setFieldValue(name, Number(value));
                  } else {
                    formik.setFieldValue(name, null);
                  }
                }}
                value={formik.values.planNumber}
                error={
                  formik.touched.planNumber && Boolean(formik.errors.planNumber)
                }
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <KeyboardDatePicker
                id="plannedStart"
                name="plannedStart"
                label="Início previsto"
                inputVariant="outlined"
                format="DD/MM/YYYY"
                cancelLabel="Cancelar"
                clearLabel="Limpar"
                minDate={MIN_DATE}
                maxDate={MAX_DATE}
                KeyboardButtonProps={{
                  'aria-label': 'change date',
                }}
                onChange={date => handleDateChange('plannedStart', date)}
                value={formik.values.plannedStart}
                error={
                  formik.touched.plannedStart &&
                  Boolean(formik.errors.plannedStart)
                }
                helperText={
                  (formik.touched.plannedStart && formik.errors.plannedStart) ||
                  DEFAULT_HELPER_TEXT
                }
                clearable
                className={classes.inputList}
              />
            </Grid>
            <Grid item xs={6}>
              <KeyboardDatePicker
                id="plannedCompletion"
                name="plannedCompletion"
                label="Término previsto"
                inputVariant="outlined"
                format="DD/MM/YYYY"
                cancelLabel="Cancelar"
                clearLabel="Limpar"
                minDate={formik.values?.plannedStart || MIN_DATE}
                maxDate={MAX_DATE}
                KeyboardButtonProps={{
                  'aria-label': 'change date',
                }}
                onChange={date => handleDateChange('plannedCompletion', date)}
                value={formik.values.plannedCompletion}
                error={
                  formik.touched.plannedCompletion &&
                  Boolean(formik.errors.plannedCompletion)
                }
                helperText={
                  (formik.touched.plannedCompletion &&
                    formik.errors.plannedCompletion) ||
                  DEFAULT_HELPER_TEXT
                }
                clearable
                className={classes.inputList}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                id="plannedDuration"
                name="plannedDuration"
                label="Duração prevista (HH:MM)"
                variant="outlined"
                onChange={formik.handleChange}
                value={formik.values.plannedDuration}
                error={
                  formik.touched.plannedDuration &&
                  Boolean(formik.errors.plannedDuration)
                }
                helperText={
                  (formik.touched.plannedDuration &&
                    formik.errors.plannedDuration) ||
                  OPTIONAL
                }
                InputProps={{ inputComponent: InputDuration }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                id="frequency"
                name="frequency"
                label="Periodicidade"
                variant="outlined"
                inputProps={{
                  type: 'number',
                }}
                onChange={event => {
                  const { value, name } = event.target;

                  if (value) {
                    formik.setFieldValue(name, Number(value));
                  } else {
                    formik.setFieldValue(name, null);
                  }
                }}
                onBlur={formik.handleBlur}
                value={formik.values.frequency}
                error={
                  formik.touched.frequency && Boolean(formik.errors.frequency)
                }
                helperText={
                  (formik.touched.frequency && formik.errors.frequency) ||
                  OPTIONAL
                }
              />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Autocomplete
                id="supplier"
                className={classes.inputList}
                options={supplierOptions}
                onChange={(e, selected) => {
                  return handleAutoCompleteChange(
                    'supplier',
                    selected as IOption,
                  );
                }}
                getOptionLabel={option => String(option.label)}
                getOptionSelected={getOptionSelected}
                noOptionsText={
                  !fieldsSearchText?.supplier
                    ? SEARCH_GUIDE_TEXT
                    : NO_OPTIONS_TEXT
                }
                filterOptions={(options, params) => {
                  const filtered = filter(options, params);

                  if (params.inputValue.length >= 3) {
                    filtered.push({
                      label: params.inputValue,
                      value: params.inputValue,
                    });
                  }
                  return filtered;
                }}
                renderInput={params => {
                  const extraProps = {
                    name: 'supplier',
                    label: 'Empresa',
                    onChange: handleFieldSearchTextChange,
                    error:
                      formik.touched.supplier &&
                      Boolean(formik.errors.supplier),
                    helperText:
                      (formik.touched.supplier && formik.errors.supplier) ||
                      DEFAULT_HELPER_TEXT,
                  };
                  return inputRenderer(
                    { ...params, ...extraProps },
                    querySupplierOptions?.isLoading,
                  );
                }}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={10}>
              <FormControl
                onChange={formik.handleChange}
                error={formik.touched.outage && Boolean(formik.errors.outage)}
              >
                <FormLabel className={classes.formLabel} component="legend">
                  <Typography className={classes.formInput}>
                    A atividade possui indisponibilidade de geração?
                  </Typography>
                  {queryOutageOptions?.isLoading && (
                    <span>
                      <CircularProgress color="inherit" size={25} />
                    </span>
                  )}
                </FormLabel>
                <RadioGroup id="outage" name="outage">
                  {outageOptions.map(({ value, label }) => (
                    <FormControlLabel
                      value={value}
                      control={<Radio color="primary" />}
                      label={label}
                    />
                  ))}
                  <FormHelperText id="outage">
                    {(formik.touched.outage && formik.errors.outage) ||
                      DEFAULT_HELPER_TEXT}
                  </FormHelperText>
                </RadioGroup>
              </FormControl>
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={10}>
              <FormControl
                onChange={formik.handleChange}
                error={
                  formik.touched.utilization &&
                  Boolean(formik.errors.utilization)
                }
              >
                <FormLabel className={classes.formLabel} component="legend">
                  <Typography className={classes.formInput}>
                    A atividade vai ser realizada em aproveitamento?
                  </Typography>
                  {queryUtilizationOptions?.isLoading && (
                    <span>
                      <CircularProgress color="inherit" size={25} />
                    </span>
                  )}
                </FormLabel>
                <RadioGroup id="utilization" name="utilization">
                  {utilizationOptions.map(({ value, label }) => (
                    <FormControlLabel
                      value={value}
                      control={<Radio color="primary" />}
                      label={label}
                    />
                  ))}
                  <FormHelperText id="utilization">
                    {(formik.touched.utilization &&
                      formik.errors.utilization) ||
                      DEFAULT_HELPER_TEXT}
                  </FormHelperText>
                </RadioGroup>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
      </form>
    );
  }, [
    classes.bodyContent,
    classes.formInput,
    classes.formLabel,
    classes.formMessage,
    classes.inputList,
    fieldsSearchText?.supplier,
    filter,
    formik,
    handleAutoCompleteChange,
    handleDateChange,
    handleFieldSearchTextChange,
    inputRenderer,
    outageOptions,
    queryMaintenanceTypeOptions?.isLoading,
    queryOutageOptions?.isLoading,
    querySupplierOptions?.isLoading,
    queryUtilizationOptions?.isLoading,
    supplierOptions,
    typeMaintenanceOptions,
    utilizationOptions,
  ]);

  const renderFooter = React.useCallback(() => {
    return (
      <div className={classes.footer}>
        <Button color="primary" onClick={onClose}>
          Cancelar
        </Button>
        <ButtonWithLoading
          disabled={!formik.dirty || !formik.isValid}
          form={FORM_ID}
          type="submit"
          color="primary"
          isLoading={loadingSaveForm}
        >
          Planejar
        </ButtonWithLoading>
      </div>
    );
  }, [classes.footer, formik.dirty, formik.isValid, loadingSaveForm, onClose]);

  return (
    <MuiPickersUtilsProvider locale={ptBR} utils={DayjsUtils}>
      <ActionsButtonsBaseModal
        title="Planejar"
        open={open}
        body={renderBody()}
        footer={renderFooter()}
        width="629px"
        onClose={onClose}
      >
        {children}
      </ActionsButtonsBaseModal>
    </MuiPickersUtilsProvider>
  );
};

export default ModalPlan;
